/* eslint-disable sonarjs/cognitive-complexity */
import React, {useEffect, useState} from 'react';
import {usePlugins} from './usePlugins';
import {withGlobals} from '../../../globalPropsContext';
import {IGalleryGlobalProps} from '../../../gallery/galleryGlobalStrategy';
import {APP_DEFINITION_ID} from '@wix/wixstores-client-core/dist/es/src/constants';
import type {BISettings, CustomTextualStyle, WixRicosTheme} from '@wix/ricos';
import {WixRicosViewer, modify, Node_Type, RichContent, TextStyle_TextAlignment} from '@wix/ricos';
import '@wix/ricos/css/ricos-viewer.global.css';
import {ISettingsColor, ISettingsFont} from '@wix/tpa-settings';

export type RichContentViewerProps = IGalleryGlobalProps & {
  content: RichContent;
  ricosContext: string;
  dataHook: string;
  stylesParams: {
    paragraphColor: ISettingsColor;
    paragraphFont: ISettingsFont;
    alignment: ISettingsFont;
    heading1Font?: ISettingsFont;
    heading1Color?: ISettingsColor;
    heading2Font?: ISettingsFont;
    heading2Color?: ISettingsColor;
    heading3Font?: ISettingsFont;
    heading3Color?: ISettingsColor;
    heading4Font?: ISettingsFont;
    heading4Color?: ISettingsColor;
    heading5Font?: ISettingsFont;
    heading5Color?: ISettingsColor;
    heading6Font?: ISettingsFont;
    heading6Color?: ISettingsColor;
    quoteFont?: ISettingsFont;
    quoteColor?: ISettingsColor;
  };
  onLoad?(): any;
};

const getAlimentContent = (ricoDescription: RichContent, styleParams: RichContentViewerProps['stylesParams']) => {
  const alignment = styleParams.alignment.value.toUpperCase() as TextStyle_TextAlignment;

  return modify(ricoDescription).set((n: any) => {
    switch (n?.type) {
      case Node_Type.GALLERY:
        return {
          ...n,
          galleryData: {
            ...n.galleryData,
            containerData: {
              ...n.galleryData?.containerData,
              alignment: n.galleryData?.containerData?.alignment || alignment,
            },
          },
        };
      case Node_Type.IMAGE:
        return {
          ...n,
          imageData: {
            ...n.imageData,
            containerData: {
              ...n.imageData?.containerData,
              alignment: n.imageData?.containerData?.alignment || alignment,
            },
          },
        };
      case Node_Type.GIF:
        return {
          ...n,
          gifData: {
            ...n.gifData,
            containerData: {
              ...n.gifData?.containerData,
              alignment: n.gifData?.containerData?.alignment || alignment,
            },
          },
        };
      case Node_Type.FILE:
        return {
          ...n,
          fileData: {
            ...n.fileData,
            containerData: {
              ...n.fileData?.containerData,
              alignment: n.fileData?.containerData?.alignment || alignment,
            },
          },
        };
      case Node_Type.CODE_BLOCK:
        return {
          ...n,
          codeBlockData: {
            ...n.codeBlockData,
            textStyle: {
              ...n.codeBlockData?.textStyle,
              textAlignment:
                n.codeBlockData?.textStyle?.textAlignment && n.codeBlockData?.textStyle?.textAlignment !== 'AUTO'
                  ? n.codeBlockData?.textStyle?.textAlignment
                  : alignment,
            },
          },
        };
      case Node_Type.PARAGRAPH:
        return {
          ...n,
          paragraphData: {
            ...n.paragraphData,
            textStyle: {
              ...n.paragraphData?.textStyle,
              textAlignment:
                n.paragraphData?.textStyle?.textAlignment && n.paragraphData?.textStyle?.textAlignment !== 'AUTO'
                  ? n.paragraphData?.textStyle?.textAlignment
                  : alignment,
            },
          },
        };
      case Node_Type.HEADING:
        return {
          ...n,
          headingData: {
            ...n.headingData,
            textStyle: {
              ...n.headingData?.textStyle,
              textAlignment:
                n.headingData?.textStyle?.textAlignment && n.headingData?.textStyle?.textAlignment !== 'AUTO'
                  ? n.headingData?.textStyle?.textAlignment
                  : alignment,
            },
          },
        };
      case Node_Type.BLOCKQUOTE:
        return {
          ...n,
          blockquoteData: {
            ...n.blockquoteData,
            textStyle: {
              ...n.blockquoteData?.textStyle,
              textAlignment:
                n.blockquoteData?.textStyle?.textAlignment && n.blockquoteData?.textStyle?.textAlignment !== 'AUTO'
                  ? n.blockquoteData?.textStyle?.textAlignment
                  : alignment,
            },
          },
        };
      case Node_Type.DIVIDER:
        return {
          ...n,
          dividerData: {
            ...n.dividerData,
            alignment: n.dividerData?.alignment || alignment,
            containerData: {
              ...n.dividerData?.containerData,
              alignment: n.dividerData?.containerData?.alignment || alignment,
            },
          },
        };
      default:
        return n;
    }
  });
};

export const RichContentViewer: React.FC<RichContentViewerProps> = withGlobals(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  ({content, ricosContext, globals, dataHook, onLoad, stylesParams}: RichContentViewerProps) => {
    const {isEditorMode, instanceId} = globals;
    const [isLoaded, setIsLoaded] = useState(false);
    const [ref, setRef] = useState<HTMLDivElement | null>(null);
    const {
      paragraphFont,
      paragraphColor,
      alignment,
      heading1Font,
      heading1Color,
      heading2Font,
      heading2Color,
      heading3Font,
      heading3Color,
      heading4Font,
      heading4Color,
      heading5Font,
      heading5Color,
      heading6Font,
      heading6Color,
      quoteFont,
      quoteColor,
    } = stylesParams;

    const biSettings: BISettings = {
      consumer: 'Online Stores - Category Page',
      platform: isEditorMode ? 'Editor' : 'Livesite',
      postId: '',
      containerId: APP_DEFINITION_ID,
      usage: ricosContext,
    };

    useEffect(() => {
      if (isLoaded) {
        return;
      }

      if (ref?.childNodes.length > 0) {
        setIsLoaded(true);
        onLoad?.();
      }
    }, [ref, isLoaded, onLoad]);

    /* istanbul ignore file */
    const customStyles: CustomTextualStyle = {
      ...(paragraphFont.style?.bold && {fontWeight: 'bold'}),
      ...(paragraphFont.style?.italic && {fontStyle: 'italic'}),
      ...(paragraphFont.style?.underline && {textDecoration: 'underline'}),
      fontSize: `${paragraphFont.size}px`,
      color: paragraphColor.value,
      ...(alignment?.value && {textAlign: alignment.value as any}),
    };

    const heading1Styles: CustomTextualStyle = heading1Font
      ? {
          ...(heading1Font.style?.bold && {fontWeight: 'bold'}),
          ...(heading1Font.style?.italic && {fontStyle: 'italic'}),
          ...(heading1Font.style?.underline && {textDecoration: 'underline'}),
          fontFamily: heading1Font.family,
          fontSize: `${heading1Font.size}px`,
          ...(heading1Color && {color: heading1Color.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const heading2Styles: CustomTextualStyle = heading2Font
      ? {
          ...(heading2Font.style?.bold && {fontWeight: 'bold'}),
          ...(heading2Font.style?.italic && {fontStyle: 'italic'}),
          ...(heading2Font.style?.underline && {textDecoration: 'underline'}),
          fontFamily: heading2Font.family,
          fontSize: `${heading2Font.size}px`,
          ...(heading2Color && {color: heading2Color.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const heading3Styles: CustomTextualStyle = heading3Font
      ? {
          ...(heading3Font.style?.bold && {fontWeight: 'bold'}),
          ...(heading3Font.style?.italic && {fontStyle: 'italic'}),
          ...(heading3Font.style?.underline && {textDecoration: 'underline'}),
          fontFamily: heading3Font.family,
          fontSize: `${heading3Font.size}px`,
          ...(heading3Color && {color: heading3Color.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const heading4Styles: CustomTextualStyle = heading4Font
      ? {
          ...(heading4Font.style?.bold && {fontWeight: 'bold'}),
          ...(heading4Font.style?.italic && {fontStyle: 'italic'}),
          ...(heading4Font.style?.underline && {textDecoration: 'underline'}),
          fontFamily: heading4Font.family,
          fontSize: `${heading4Font.size}px`,
          ...(heading4Color && {color: heading4Color.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const heading5Styles: CustomTextualStyle = heading5Font
      ? {
          ...(heading5Font.style?.bold && {fontWeight: 'bold'}),
          ...(heading5Font.style?.italic && {fontStyle: 'italic'}),
          ...(heading5Font.style?.underline && {textDecoration: 'underline'}),
          fontFamily: heading5Font.family,
          fontSize: `${heading5Font.size}px`,
          ...(heading5Color && {color: heading5Color.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const heading6Styles: CustomTextualStyle = heading6Font
      ? {
          ...(heading6Font.style?.bold && {fontWeight: 'bold'}),
          ...(heading6Font.style?.italic && {fontStyle: 'italic'}),
          ...(heading6Font.style?.underline && {textDecoration: 'underline'}),
          fontFamily: heading6Font.family,
          fontSize: `${heading6Font.size}px`,
          ...(heading6Color && {color: heading6Color.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const quoteStyles: CustomTextualStyle = quoteFont
      ? {
          ...(quoteFont.style?.bold && {fontWeight: 'bold'}),
          ...(quoteFont.style?.italic && {fontStyle: 'italic'}),
          ...(quoteFont.style?.underline && {textDecoration: 'underline'}),
          fontFamily: quoteFont.family,
          fontSize: `${quoteFont.size}px`,
          ...(quoteColor && {color: quoteColor.value}),
          ...(alignment?.value && {textAlign: alignment.value as any}),
        }
      : customStyles;

    const theme: WixRicosTheme = {
      palette: {
        textColor: paragraphColor.value,
      },
      typography: {
        fontFamily: paragraphFont.family,
      },
      customStyles: {
        p: customStyles,
        h1: heading1Styles,
        h2: heading2Styles,
        h3: heading3Styles,
        h4: heading4Styles,
        h5: heading5Styles,
        h6: heading6Styles,
        link: customStyles,
        hashtag: customStyles,
        quote: quoteStyles,
      },
    };

    let ricoDescription = content;

    if (alignment?.value) {
      ricoDescription = getAlimentContent(ricoDescription, stylesParams);
    }

    const isBrowser = typeof document !== 'undefined';
    const mediaSettings = /* istanbul ignore next */ isBrowser ? {fullscreenProps: {container: document?.body}} : {};

    return (
      <div data-hook={dataHook} ref={(r) => setRef(r)}>
        <WixRicosViewer
          theme={theme}
          biSettings={biSettings}
          mediaSettings={mediaSettings}
          content={ricoDescription}
          plugins={usePlugins()}
          instance={instanceId}
        />
      </div>
    );
  }
);
